<template>
	<transition
		v-if="campaignEnabled"
		enter-active-class="transition-all duration-300 ease-out"
		enter-from-class="opacity-0"
		enter-to-class="opacity-100"
		leave-active-class="transition-all duration-200 ease-in"
		leave-from-class="opacity-100"
		leave-to-class="opacity-0"
	>
		<Teleport to="body">
			<div
				v-if="visible"
				class="fixed inset-0 z-[10000] print:hidden flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm"
				role="dialog"
				aria-modal="true"
				aria-label="Free download: The Family That Sings"
				@click.self="dismiss"
			>
				<transition
					enter-active-class="transition-all duration-400 ease-out"
					enter-from-class="opacity-0 scale-95 translate-y-4"
					enter-to-class="opacity-100 scale-100 translate-y-0"
					appear
				>
					<div
						v-if="visible"
						class="relative w-full max-w-[40rem] rounded-2xl overflow-hidden shadow-2xl bg-[#2f3f41] text-white ring-1 ring-black/30"
					>
						<button
							@click="dismiss"
							class="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-colors"
							aria-label="Dismiss"
						>
							<i class="fas fa-times text-base" />
						</button>

						<div class="grid grid-cols-1 sm:grid-cols-[42%_58%]">
							<!-- Cover -->
							<div class="relative hidden sm:block bg-[#263133]">
								<div class="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none"
									style="background-image:
										radial-gradient(circle at 30% 20%, rgba(204,157,79,0.55), transparent 60%),
										radial-gradient(circle at 70% 85%, rgba(78,111,112,0.55), transparent 60%);" />
								<div class="relative h-full flex items-center justify-center p-6">
									<img
										:src="coverUrl"
										alt="The Family That Sings"
										class="w-full max-w-[14rem] rounded-lg shadow-2xl ring-1 ring-black/40"
									/>
								</div>
							</div>

							<!-- Content -->
							<div class="relative px-6 py-8 sm:px-8">
								<div class="absolute inset-0 opacity-25 pointer-events-none sm:hidden">
									<img :src="coverUrl" class="w-full h-full object-cover" alt="" />
								</div>
								<div class="absolute inset-0 bg-gradient-to-b from-[#2f3f41]/70 via-[#2f3f41]/85 to-[#2f3f41] pointer-events-none sm:hidden" />

								<div class="relative">
									<!-- Offer state -->
									<template v-if="!submitted">
										<div class="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#dcae63]">
											Free Digital Download
										</div>
										<div class="font-serif font-bold text-2xl sm:text-3xl leading-tight mt-3">
											Get <span class="italic">The Family That Sings</span> &mdash; Free
										</div>
										<div class="font-serif text-base text-white/80 mt-3 leading-snug">
											Fill your home with hymns. Enter your email and we&rsquo;ll send you the
											complete book as a free digital download &mdash; a simple resource for
											every family to learn and sing together.
										</div>

										<form class="mt-6 space-y-3" @submit.prevent="submit">
											<input
												v-model="name"
												type="text"
												autocomplete="name"
												placeholder="First name (optional)"
												class="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 ring-1 ring-white/20 focus:ring-2 focus:ring-[#cc9d4f] focus:outline-none transition"
											/>
											<input
												v-model="email"
												type="email"
												required
												autocomplete="email"
												placeholder="Your email address"
												class="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 ring-1 ring-white/20 focus:ring-2 focus:ring-[#cc9d4f] focus:outline-none transition"
											/>
											<p v-if="error" class="text-sm text-red-300">{{ error }}</p>
											<button
												type="submit"
												:disabled="submitting"
												class="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#cc9d4f] text-white text-sm font-semibold tracking-wide shadow-lg hover:bg-[#b98b44] hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed group"
											>
												<span v-if="submitting">Sending&hellip;</span>
												<template v-else>
													Send Me the Free Book
													<i class="fas fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" />
												</template>
											</button>
										</form>

										<button
											@click="dismiss"
											class="block w-full mt-4 text-xs text-white/50 hover:text-white/80 tracking-wide transition-colors"
										>
											No thanks
										</button>
									</template>

									<!-- Success state -->
									<template v-else>
										<div class="text-center sm:text-left">
											<i class="fas fa-check-circle text-4xl text-[#dcae63]" />
											<div class="font-serif font-bold text-2xl leading-tight mt-4">
												Your copy is ready!
											</div>
											<div class="font-serif text-base text-white/80 mt-3 leading-snug">
												Thank you for signing up. A copy is on its way to your inbox &mdash;
												or download it right now:
											</div>
											<a
												:href="downloadUrl"
												target="_blank"
												rel="noopener"
												@click="trackDownload"
												class="mt-6 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#cc9d4f] text-white text-sm font-semibold tracking-wide shadow-lg hover:bg-[#b98b44] hover:shadow-xl transition-all group"
											>
												<i class="fas fa-download text-xs" />
												Download the Book
											</a>
											<button
												@click="dismiss"
												class="block w-full sm:w-auto mt-4 text-xs text-white/50 hover:text-white/80 tracking-wide transition-colors"
											>
												Close
											</button>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
				</transition>
			</div>
		</Teleport>
	</transition>
</template>

<script>
import createCampaignElementMixin from '../../helpers/campaignElementMixin'
import { fireLeadConversion, fireFormError } from '../../helpers/leadConversion.js'
import {
	shouldSuppressPopup,
	markDismissed,
	anyPromoPopupShown,
	markPromoPopupShown,
} from '../../helpers/campaignSuppress'
import { apiDomain, port, useDevAPI } from '../../../../config/config/settings.json'

const CAMPAIGN = 'the-family-that-sings'
const COVER_URL = 'https://cfl-mango.s3.us-east-1.amazonaws.com/assets/products/69b30dbf6a8daf78584b4538/image.jpg?updated=1773342144205'
const DOWNLOAD_URL = 'https://cfl-mango.s3.amazonaws.com/uploads/downloads/The%20Family%20the%20Sings.pdf'
const DISMISS_KEY = 'ftsBookDismissed'
const SHOWN_KEY = 'ftsBookShown'
// Fires before the 25s Family Worship slide-in so the book offer wins the
// "one popup per session" race (and the shared flag keeps the others suppressed).
const DELAY_MS = 12_000
const ARM_EXIT_DELAY_MS = 5_000
const TOP_THRESHOLD_PX = 8
const EXCLUDED_PATTERNS = [
	// English promos don't belong on the Spanish/Chinese sections (HAP-4833).
	/^\/(es|cn)(\/|$|\?)/i,
	/checkout/i,
	/confirmation/i,
	/starter-kit/i,
	/thank/i,
	/embed/i,
	// The book's own product page — don't offer it free where it's being sold.
	/^\/products\/69b30dbf6a8daf78584b4538/i,
]

function apiBase() {
	try {
		const isDev = import.meta.env?.DEV
		return useDevAPI && isDev ? `http://localhost:${port}` : apiDomain
	} catch {
		return ''
	}
}

export default {
	mixins: [createCampaignElementMixin('book-offer-popup')],
	inject: ['breakpoints'],
	data() {
		return {
			visible: false,
			armed: false,
			name: '',
			email: '',
			submitting: false,
			submitted: false,
			error: '',
			coverUrl: COVER_URL,
			downloadUrl: DOWNLOAD_URL,
		}
	},
	computed: {
		onExcludedRoute() {
			const path = this.$route?.path || ''
			return EXCLUDED_PATTERNS.some(re => re.test(path))
		},
		isDesktop() {
			return !!this.breakpoints?.md
		},
	},
	mounted() {
		// Admin artwork preview: show immediately, skip the timer/exit-intent gate.
		if (this.preview) {
			this.visible = true
			return
		}
		if (shouldSuppressPopup(DISMISS_KEY)) return
		try {
			if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return
		} catch {}

		// Primary trigger: timed.
		this._timer = window.setTimeout(() => {
			this.armed = true
			this.maybeShow()
		}, DELAY_MS)

		// Secondary trigger (desktop): exit-intent makes the offer feel unavoidable.
		if (this.isDesktop) {
			this._armTimer = window.setTimeout(() => {
				this.armed = true
				this._onMouseOut = this.handleMouseOut.bind(this)
				document.addEventListener('mouseout', this._onMouseOut)
			}, ARM_EXIT_DELAY_MS)
		}
	},
	beforeUnmount() {
		if (this._timer) window.clearTimeout(this._timer)
		if (this._armTimer) window.clearTimeout(this._armTimer)
		if (this._onMouseOut) document.removeEventListener('mouseout', this._onMouseOut)
		document.body.style.overflow = ''
	},
	watch: {
		visible(v) {
			document.body.style.overflow = v ? 'hidden' : ''
		},
	},
	methods: {
		handleMouseOut(e) {
			if (!this.armed || this.visible) return
			if (this.onExcludedRoute) return
			if (e.relatedTarget || e.toElement) return
			if (e.clientY > TOP_THRESHOLD_PX) return
			this.maybeShow()
		},
		maybeShow() {
			// A pop-up that is switched OFF must not consume the one-promo-per-session slot.
			// Without this, a disabled pop-up still fired its timer, called markPromoPopupShown()
			// and silently suppressed a pop-up the admin had switched ON (HAP-3654). Checked here
			// rather than in mounted() because the flags load asynchronously — at mount time this
			// still holds the offline fallback value.
			if (!this.campaignEnabled) return
			if (this.visible) return
			if (this.onExcludedRoute) return
			// Re-check at show time — visitor may have signed up/dismissed since arming.
			if (shouldSuppressPopup(DISMISS_KEY)) return
			// Only one promo pop-up per session — stand down if another already fired.
			if (anyPromoPopupShown()) return
			try {
				if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return
				window.sessionStorage.setItem(SHOWN_KEY, '1')
			} catch {}
			this.visible = true
			markPromoPopupShown()
			this.trackImpression()
		},
		async submit() {
			if (this.submitting) return
			this.error = ''
			const email = (this.email || '').trim().toLowerCase()
			if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
				this.error = 'Please enter a valid email address.'
				return
			}
			this.submitting = true
			try {
				const res = await fetch(`${apiBase()}/controllers/campaignSignup`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'omit',
					body: JSON.stringify({
						name: this.name,
						email,
						campaign: CAMPAIGN,
						source: 'book-offer-popup',
						sourceElement: 'book-offer-popup',
					}),
				})
				// Success is shown either way (below) — only the conversion is gated on a
				// real acceptance, since Mango answers 200 on rejection too (HAP-3876).
				const body = await res.json().catch(() => null)
				if (body?.success) {
					fireLeadConversion({
						formName: 'family_that_sings_popup',
						campaign: CAMPAIGN,
						sourceElement: 'book-offer-popup',
					})
				} else {
					fireFormError({ formName: 'family_that_sings_popup' })
				}
				this.submitted = true
				this.markConverted()
				this.trackSignup()
			} catch (e) {
				// Don't strand the visitor — the signup endpoint is fire-and-forget on
				// the server, so a network hiccup still likely succeeded. Show success.
				fireFormError({ formName: 'family_that_sings_popup' })
				this.submitted = true
				this.markConverted()
				this.trackSignup()
			} finally {
				this.submitting = false
			}
		},
		markConverted() {
			// Persist signup so neither this popup nor the Family Worship popups nag
			// this visitor again. `fwKitConverted` is read by campaignSuppress.hasSignedUp().
			try {
				window.localStorage.setItem(DISMISS_KEY, '1')
				window.localStorage.setItem('fwKitConverted', '1')
				window.sessionStorage.setItem('fwKitConverted', '1')
			} catch {}
		},
		dismiss() {
			this.visible = false
			markDismissed(DISMISS_KEY)
			if (!this.submitted) this.trackCampaignDismiss()
		},
		trackImpression() {
			this.trackCampaignImpression()
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_that_sings_book_impression', {
						event_category: 'engagement',
						event_label: 'book-offer-popup',
						route: this.$route?.path,
					})
				}
			} catch {}
		},
		trackSignup() {
			this.trackCampaignClick({ action: 'signup' })
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_that_sings_book_signup', {
						event_category: 'engagement',
						event_label: 'book-offer-popup',
						route: this.$route?.path,
					})
				}
			} catch {}
		},
		trackDownload() {
			this.trackCampaignClick({ action: 'download', link_url: this.downloadUrl })
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_that_sings_book_download', {
						event_category: 'engagement',
						event_label: 'book-offer-popup',
						link_url: this.downloadUrl,
						route: this.$route?.path,
					})
				}
			} catch {}
		},
	},
}
</script>
